/**
 * Project Templates listing page for /admin/project-templates route.
 * Lists available project templates and allows creating new ones.
 * Admin-only access. Uses KiiList with client-side search and scope filter.
 */

import { useState, useCallback, useMemo } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { adminRoutes, useWorkspaceUrl } from "../lib/routes";
import { PageHeader } from "../components/layout/PageHeader";
import {
  getProjectTemplates,
  createProjectTemplate,
  type ProjectTemplate,
} from "../services/naraDoor";
import { Layers, Plus, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useProfile } from "../contexts/ProfileContext";
import { KiiList, KiiDialogLayout, type KiiListColumn, type FilterConfig, type FilterValues } from "../components/kii";
import { TableRow, TableCell } from "../components/ui/table";

const columns: KiiListColumn[] = [
  { key: "name", label: "Name" },
  { key: "description", label: "Description" },
  { key: "scope", label: "Scope" },
];

const scopeFilterOptions = [
  { value: "global", label: "Global" },
  { value: "workspace", label: "Workspace" },
];

const scopeFilters: FilterConfig[] = [
  {
    id: "scope",
    label: "Scope",
    placeholder: "All scopes",
    colorVariant: "primary",
    multiple: false,
    options: scopeFilterOptions,
  },
];

export function ProjectTemplates() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin, isLoading: isProfileLoading } = useProfile();
  const urls = useWorkspaceUrl();

  // Search & filter state
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [filterValues, setFilterValues] = useState<FilterValues>({ scope: null });

  // Create template dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [templateScope, setTemplateScope] = useState<"global" | "workspace">("workspace");
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Redirect non-admins to dashboard
  if (!isProfileLoading && !isAdmin) {
    return <Navigate to={urls?.dashboard || "/dashboard"} replace />;
  }

  const { data, isLoading } = useQuery({
    queryKey: ["projectTemplates"],
    queryFn: () => getProjectTemplates(),
    enabled: isAdmin,
  });

  const allTemplates = data?.templates || [];

  // Client-side search + scope filter
  const templates = useMemo(() => {
    let filtered = allTemplates;
    const scopeFilter = filterValues.scope as string | null;
    if (scopeFilter) {
      filtered = filtered.filter((t) => t.scope === scopeFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          t.description?.toLowerCase().includes(q)
      );
    }
    return filtered;
  }, [allTemplates, filterValues.scope, search]);

  const handleSearch = useCallback((query: string) => {
    setSearch(query.trim());
  }, []);

  const handleSearchQueryChange = useCallback((query: string) => {
    setSearchInput(query);
  }, []);

  const handleFilterChange = useCallback((newFilters: FilterValues) => {
    setFilterValues(newFilters);
  }, []);

  const handleSaveTemplate = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmedName = templateName.trim();
    const trimmedDescription = templateDescription.trim();
    setCreateError(null);

    if (!trimmedName) {
      setCreateError(t("projectTemplates.dialog.nameRequired"));
      return;
    }

    setIsSavingTemplate(true);
    try {
      const newTemplate = await createProjectTemplate({
        name: trimmedName,
        description: trimmedDescription || undefined,
        scope: templateScope,
      });
      setShowCreateDialog(false);
      setTemplateName("");
      setTemplateDescription("");
      setTemplateScope("workspace");
      setCreateError(null);
      queryClient.invalidateQueries({ queryKey: ["projectTemplates"] });
      navigate(adminRoutes.projectTemplate(newTemplate.id));
    } catch (error: any) {
      setCreateError(error.message || t("projectTemplates.dialog.createFailed"));
    } finally {
      setIsSavingTemplate(false);
    }
  };

  return (
    <div>
      <PageHeader
        title={t("projectTemplates.title")}
        description={t("projectTemplates.description")}
        isAdmin
        breadcrumbs={[
          { label: t("projectTemplates.breadcrumbs.admin") },
          { label: t("projectTemplates.breadcrumbs.projectTemplates") },
        ]}
        actions={
          <button
            onClick={() => setShowCreateDialog(true)}
            className="inline-flex items-center gap-2 px-3 py-2 bg-nara-orange text-nara-charcoal font-medium rounded-lg hover:bg-nara-orange/90 transition-colors"
          >
            <Plus className="h-4 w-4" />
            {t("projectTemplates.createTemplate")}
          </button>
        }
      />

      <KiiList<ProjectTemplate>
        filters={scopeFilters}
        filterValues={filterValues}
        onFilterChange={handleFilterChange}
        searchQuery={searchInput}
        onSearchQueryChange={handleSearchQueryChange}
        onSearch={handleSearch}
        columns={columns}
        data={templates}
        isLoading={isLoading}
        emptyMessage={search || filterValues.scope ? t("projectTemplates.noMatchingTemplates") : t("projectTemplates.noTemplates")}
        emptyIcon={<Layers className="h-10 w-10 text-muted-foreground" />}
        showCount={true}
        countLabel={(count) => t("projectTemplates.templateCount", { count })}
        onRowClick={(template) => navigate(adminRoutes.projectTemplate(template.id))}
        renderRow={(template) => (
          <TableRow key={template.id}>
            <TableCell>
              <div className="flex items-center gap-3">
                <Layers className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="font-medium">{template.name}</span>
              </div>
            </TableCell>
            <TableCell className="text-muted-foreground">
              {template.description || "\u2014"}
            </TableCell>
            <TableCell>
              {template.scope === "global" ? (
                <span className="text-sm text-muted-foreground">
                  {t("projectTemplates.scope.global")}
                </span>
              ) : template.workspace_id ? (
                <Link
                  to={adminRoutes.workspace(template.workspace_id)}
                  onClick={(e) => e.stopPropagation()}
                  className="text-sm text-nara-orange hover:underline"
                >
                  {template.workspace_name || template.workspace_id}
                </Link>
              ) : (template.workspace_ids?.length ?? 0) > 0 ? (
                <span className="text-sm text-muted-foreground">
                  {template.workspace_ids!.length} {template.workspace_ids!.length === 1 ? "workspace" : "workspaces"}
                </span>
              ) : (
                <span className="text-sm text-muted-foreground">
                  {t("projectTemplates.scope.workspace")}
                </span>
              )}
            </TableCell>
          </TableRow>
        )}
      />

      {/* Create Template Dialog */}
      <KiiDialogLayout
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        title={t("projectTemplates.dialog.createTitle")}
        description={t("projectTemplates.dialog.createDescription")}
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => {
                setShowCreateDialog(false);
                setCreateError(null);
                setTemplateScope("workspace");
              }}
              disabled={isSavingTemplate}
              className="px-4 py-2 text-foreground border border-border rounded-lg hover:bg-accent transition-colors disabled:opacity-50"
            >
              {t("projectTemplates.dialog.cancel")}
            </button>
            <button
              onClick={() => handleSaveTemplate()}
              disabled={!templateName.trim() || isSavingTemplate}
              className="inline-flex items-center gap-2 px-4 py-2 bg-nara-orange text-nara-charcoal font-medium rounded-lg hover:bg-nara-orange/90 transition-colors disabled:opacity-50"
            >
              {isSavingTemplate && <Loader2 className="h-4 w-4 animate-spin" />}
              {isSavingTemplate ? t("projectTemplates.dialog.creating") : t("projectTemplates.dialog.create")}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label
              htmlFor="templateName"
              className="block text-sm font-medium text-foreground mb-1.5"
            >
              {t("projectTemplates.dialog.nameLabel")} <span className="text-red-500">*</span>
            </label>
            <input
              id="templateName"
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder={t("projectTemplates.dialog.namePlaceholder")}
              maxLength={255}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
              onKeyDown={(e) => {
                if (e.key === "Enter" && templateName.trim()) handleSaveTemplate();
              }}
              autoFocus
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t("projectTemplates.dialog.nameHint")}
            </p>
          </div>
          <div>
            <label
              htmlFor="templateScope"
              className="block text-sm font-medium text-foreground mb-1.5"
            >
              Scope
            </label>
            <select
              id="templateScope"
              value={templateScope}
              onChange={(e) => setTemplateScope(e.target.value as "global" | "workspace")}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
            >
              <option value="workspace">Workspace — Restricted to specific workspaces</option>
              <option value="global">Global — Available to all workspaces</option>
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              Global templates are available to all workspaces. Workspace templates are restricted.
            </p>
          </div>
          <div>
            <label
              htmlFor="templateDescription"
              className="block text-sm font-medium text-foreground mb-1.5"
            >
              {t("projectTemplates.dialog.descriptionLabel")}
            </label>
            <textarea
              id="templateDescription"
              value={templateDescription}
              onChange={(e) => setTemplateDescription(e.target.value)}
              placeholder={t("projectTemplates.dialog.descriptionPlaceholder")}
              className="w-full h-24 px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-nara-orange/50 resize-none"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t("projectTemplates.dialog.descriptionHint")}
            </p>
          </div>
          {createError && (
            <p className="text-sm text-red-500">{createError}</p>
          )}
        </div>
      </KiiDialogLayout>
    </div>
  );
}
